;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import { useEffect, useState } from 'react';

import { createClient } from '../utils/supabase';

/**
 * Custom hook for realtime favorite count updates
 * @param {string} itemType - The type of item (product, recipe, etc.)
 * @param {string} itemId - The ID of the item
 * @param {boolean} enabled - Whether to enable realtime updates
 * @returns {number|null} The current count or null if not loaded
 */
export const useRealtimeFavoriteCount = (itemType, itemId, enabled = false) => {
    const [count, setCount] = useState(null);

    useEffect(() => {
        if (!enabled || !itemType || !itemId) {
            return;
        }

        let channel = null;
        let supabase = null;
        let isCleanedUp = false;

        const setupRealtimeSubscription = async () => {
            if (isCleanedUp) {
                console.log(
                    `⚠️ Skipping subscription setup - already cleaned up for ${itemType}:${itemId}`
                );
                return;
            }
            try {
                console.log(`Setting up realtime for ${itemType}:${itemId}`);

                supabase = createClient();

                // First, get the initial count
                const { data, error } = await supabase
                    .from('favorite_counts')
                    .select('total_count')
                    .eq('item_type', itemType)
                    .eq('item_id', itemId)
                    .single();

                if (error && error.code !== 'PGRST116') {
                    // PGRST116 is "not found" - that's okay, count is 0
                    console.error('Error fetching initial count:', error);
                    return;
                }

                const initialCount = data?.total_count || 0;
                setCount(initialCount);

                // Set up realtime subscription for this specific item
                const timestamp = Date.now();
                const channelName = `favorite_count_${itemType}_${itemId}_${timestamp}`;
                const filterString = `item_id=eq.${itemId}`;

                channel = supabase
                    .channel(channelName)
                    .on(
                        'postgres_changes',
                        {
                            event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
                            schema: 'public',
                            table: 'favorite_counts',
                            filter: filterString,
                        },
                        payload => {
                            if (payload.eventType === 'DELETE') {
                                setCount(0);
                            } else if (payload.new) {
                                const newCount = payload.new.total_count || 0;

                                setCount(newCount);
                            }
                        }
                    )
                    .subscribe((status, err) => {
                        if (status === 'SUBSCRIBED') {
                            console.log(
                                `✅ Subscribed to realtime updates for ${itemType}:${itemId} (${channelName})`
                            );
                        } else if (status === 'CHANNEL_ERROR') {
                            console.warn('❌ Realtime subscription error:', {
                                itemType,
                                itemId,
                                channelName,
                                filterString,
                                error: err,
                                message:
                                    err?.message || 'Channel error occurred',
                            });
                        } else if (status === 'TIMED_OUT') {
                            console.error(
                                '⏰ Realtime subscription timed out:',
                                {
                                    itemType,
                                    itemId,
                                    channelName,
                                    filterString,
                                    suggestion:
                                        'Check network connection and Supabase realtime settings',
                                }
                            );
                        } else if (status === 'CLOSED') {
                            console.log('🔒 Realtime subscription closed:', {
                                itemType,
                                itemId,
                                channelName,
                            });
                        }
                    });
            } catch (error) {
                console.error('Error setting up realtime subscription:', error);
            }
        };

        setupRealtimeSubscription();

        // Cleanup subscription on unmount or when dependencies change
        return () => {
            isCleanedUp = true;
            if (channel && supabase) {
                // Properly remove the channel to prevent binding conflicts
                supabase.removeChannel(channel);
                console.log(
                    `🧹 Cleaned up realtime channel for ${itemType}:${itemId}`
                );
            }
        };
    }, [itemType, itemId, enabled]);

    return count;
};
